import React from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import styles from './index.module.scss'
import dayjs from '../../utilities/dayjs'
import DefaultBillingStatusInputs from '../default-billing-status-inputs'
import { TextareaInput } from '../TextareaInput'
import { DatePickerInput } from '../DatePickerInput'
import { TimeInput } from '../TimeInput'
import { useSelector } from 'react-redux'
import { dayOptions, validateAssetsUtil } from '../create-ticket-recurring/utils'
import AssetRecurringFormInputs from '../create-ticket-recurring/AssetRecurringFormInputs'
import { parseOrNull } from '../../utilities/randomUtilities'
import cn from 'classnames'

export default function NewTicketForm ({
  onSubmit,
  resourceTypes,
  ticketTypes,
  billingStatuses,
  ticketTypeBillingStatusSelected,
  setTicketTypeBillingStatusSelected,
  resourceTypeBillingStatusSelected,
  setResourceTypeBillingStatusSelected,
  disabled = false,
  serviceFrequency,
  feeTypes,
  users,
  initialAssetValues
}) {
  const { hauler } = useSelector(({ user: { hauler } }) => ({ hauler }))
  const isRecurring = serviceFrequency === 'RECURRING' && hauler.isRecurringServiceEnabled

  function clearDescription (args, state) {
    const [ticketFeeIndex] = args
    state.fields[`${ticketFeeIndex}.description`].change(null)
  }

  const handleFormSubmit = (values) => {
    const finalFormValues = isRecurring ? transformFormValues(values) : values
    onSubmit(finalFormValues)
  }

  const transformFormValues = (values) => {
    return {
      ...values,
      assetDetails: (values.assetDetails || []).map(asset => {
        const {
          ticketFees = [],
          ...rest
        } = asset

        const newTicketFees = ticketFees.map(ticketFee => ({
          amount: parseFloat(ticketFee.amount),
          ticketFeeId: ticketFee.id,
          description: ticketFee.description,
          quantity: Number(ticketFee.quantity),
          feeTypeId: ticketFee.feeType?.id
        }))

        return {
          ...rest,
          serviceDailyFrequency: parseOrNull(rest.serviceDailyFrequency, 'int'),
          serviceDate: Array.isArray(rest.serviceDate) && rest.serviceDate.length > 0
            ? rest.serviceDate.map(d => parseInt(d))
            : null,
          driverId: rest.driverId === 'null' ? null : rest.driverId,
          newTicketFees
        }
      })
    }
  }

  return (
    <Form
      mutators={{ ...arrayMutators, clearDescription }}
      validate={validate(isRecurring)}
      onSubmit={handleFormSubmit}>
      {({ handleSubmit, pristine, form, invalid, values }) => {
        const formState = form.getState()
        const billingStatusManuallyUpdated = formState.dirtyFields?.billingStatus && formState.visited?.billingStatus
        return (
          <form onSubmit={handleSubmit} autoComplete='off'>
            <DefaultBillingStatusInputs
              billingStatuses={billingStatuses}
              billingStatusManuallyUpdated={billingStatusManuallyUpdated}
              resourceTypeBillingStatusSelected={resourceTypeBillingStatusSelected}
              resourceTypes={resourceTypes}
              setResourceTypeBillingStatusSelected={setResourceTypeBillingStatusSelected}
              setTicketTypeBillingStatusSelected={setTicketTypeBillingStatusSelected}
              ticketTypeBillingStatusSelected={ticketTypeBillingStatusSelected}
              ticketTypes={ticketTypes}
              values={values}
              serviceFrequency={serviceFrequency}
              form={form}
              initialAssetValues={initialAssetValues}
            />
            {isRecurring && (
              <FieldArray name='assetDetails'>
                {({ fields }) => (
                  <>
                    {fields.map((name, index) => (
                      <AssetRecurringFormInputs
                        assetIndex={index}
                        key={name}
                        name={name}
                        resourceTypes={resourceTypes}
                        dayOptions={dayOptions}
                        feeTypes={feeTypes}
                        onRemove={() => fields.remove(index)}
                        form={form}
                        users={users}
                      />
                    ))}
                  </>
                )}
              </FieldArray>
            )}

            {!isRecurring && (
              <Field
                name='date'
                label='Ticket Date'
                component={DatePickerInput}
              />
            )}

            <Field
              component={TextareaInput}
              label='Comment (Optional)'
              name='comment'
              placeholder='(Comments are for each individual ticket and can be used for placement details, messages for drivers, etc.
              Drivers can view comments on the mobile app.)'
            />

            <Field
              component={TextareaInput}
              label='Billing Notes (Optional)'
              name='billingNotes'
              maxLength='1000'
              placeholder='(Billing Notes can be used to track quoted prices or any other billing information.
              Drivers do not see billing notes on the mobile app.)'
            />

            <div className={styles.requestedTimes}>
              <div className={styles.timeInput}>
                <Field
                  name='requestedStartTime'
                  label='Customer Requested Start Time'
                  component={TimeInput}
                />
              </div>
              <div className={styles.timeInput}>
                <Field
                  name='requestedEndTime'
                  label='Customer Requested End Time'
                  component={TimeInput}
                />
              </div>
            </div>

            {isRecurring && (
              <div className={cn('dis-panel', styles.messageContainer)}>
                <span className={styles.messageContainerTitle}>Please note:</span>
                <ol className={styles.messageList}>
                  <li>
                    It may take some time to create multiple tickets. You will receive a notification when all the tickets have been created or if an error occurs.
                  </li>
                  <li>
                    When a recurring ticket is created, the system will automatically generate tickets for the upcoming 90 days only.
                  </li>
                </ol>
              </div>
            )}

            <div className={styles.buttonContainer}>
              {isRecurring
                ? (
                  <button
                    type='submit'
                    className='dis-btn dis-btn-primary dis-btn-lg'
                    disabled={invalid || pristine || disabled}>
                    Create Recurring Tickets
                    <i className='material-icons dis-btn-icon'>add_circle</i>
                  </button>
                  )
                : (
                  <>
                    <button
                      type='submit'
                      className='dis-btn dis-btn-blank dis-btn-lg'
                      disabled={invalid || pristine || disabled}
                      onClick={() => form.change('navigateToFees', false)}>
                      Create Ticket
                    </button>
                    <button
                      type='submit'
                      className='dis-btn dis-btn-primary dis-btn-lg'
                      disabled={invalid || pristine || disabled}
                      onClick={() => form.change('navigateToFees', true)}>
                      Create Ticket & Add Fees
                      <i className='material-icons dis-btn-icon'>add_circle</i>
                    </button>
                  </>
                  )}
            </div>
          </form>
        )
      }}
    </Form>
  )
}

NewTicketForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  resourceTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    shortCode: PropTypes.string.isRequired
  })).isRequired,
  ticketTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    shortCode: PropTypes.string.isRequired
  })).isRequired,
  billingStatuses: PropTypes.object.isRequired,
  ticketTypeBillingStatusSelected: PropTypes.string,
  setTicketTypeBillingStatusSelected: PropTypes.func,
  resourceTypeBillingStatusSelected: PropTypes.string,
  setResourceTypeBillingStatusSelected: PropTypes.func,
  disabled: PropTypes.bool,
  serviceFrequency: PropTypes.string,
  feeTypes: PropTypes.array.isRequired,
  users: PropTypes.array,
  initialAssetValues: PropTypes.object
}

function validate (isRecurring) {
  return function (values) {
    const errors = {}

    if (!isRecurring && !values.date) {
      errors.date = 'Required'
    }

    if (!values.ticketTypeId) {
      errors.ticketTypeId = 'Required'
    }

    if (!isRecurring && !values.resourceTypeId) {
      errors.resourceTypeId = 'Required'
    }

    if (values.requestedStartTime && !values.requestedEndTime) {
      errors.requestedEndTime = 'Required if Start Time'
    }

    if (values.requestedEndTime && !values.requestedStartTime) {
      errors.requestedStartTime = 'Required if End Time'
    }

    const formattedRequestedStartTime = dayjs(values.requestedStartTime, 'HH:mm')
    const formattedRequestedEndTime = dayjs(values.requestedEndTime, 'HH:mm')

    if (
      values.requestedStartTime &&
      values.requestedEndTime &&
      (formattedRequestedEndTime.isBefore(formattedRequestedStartTime) ||
        formattedRequestedEndTime.isSame(formattedRequestedStartTime))
    ) {
      errors.requestedEndTime = 'End Time must be after Start Time'
      errors.requestedStartTime = 'Start Time must be before End Time'
    }

    if (isRecurring) {
      if (!values?.assetDetails?.length) errors.assetTypeSelect = 'Required'
      const assetErrors = validateAssetsUtil(values.assetDetails)
      if (assetErrors) {
        errors.assetDetails = assetErrors
      }
    }

    return errors
  }
}
